import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { DropdownModule } from 'primeng/dropdown';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';
import { EntityDialogData } from '../../services/entity-dialog.service';
import { EntityDefinition } from '../../types/entity-definition.interface';
import { EntityFormDeleteAction, EntityFormValue } from '../../types/entity-form-result.interface';
import { Asset } from '../../types/asset.interface';
import { GenericEntityFormComponent } from '../generic/generic-entity-form.component';

@Component({
  selector: 'app-asset-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    CheckboxModule,
    DropdownModule,
    GenericEntityFormComponent,
  ],
  templateUrl: './asset-form.component.html',
})
export class AssetFormComponent implements OnInit {
  @Input() entityDefinition?: EntityDefinition<Asset>;
  @Input() entity?: Asset;
  @Input() parentId?: number;
  @Input() mode: 'create' | 'edit' = 'create';
  @Input() additionalData?: Record<string, unknown>;

  @Output() formSubmit = new EventEmitter<EntityFormValue<Asset>>();
  @Output() formCancel = new EventEmitter<void>();
  @Output() deleteEntity = new EventEmitter<Asset>();

  private readonly config = inject<DynamicDialogConfig<EntityDialogData<Asset>> | null>(DynamicDialogConfig, {
    optional: true,
  });
  private readonly dialogRef = inject<DynamicDialogRef<AssetFormComponent> | null>(DynamicDialogRef, { optional: true });

  private directDeleteCallback?: (entity: Asset) => void;

  ngOnInit(): void {
    const data = this.config?.data;
    if (!data) {
      return;
    }
    this.entityDefinition = data.entityDefinition;
    this.mode = data.mode === 'edit' ? 'edit' : 'create';
    if (data.entity !== undefined) this.entity = data.entity;
    if (data.parentId !== undefined) this.parentId = data.parentId;
    if (data.additionalData !== undefined) this.additionalData = data.additionalData;
    if (data.onDelete !== undefined) this.directDeleteCallback = data.onDelete;
  }

  onFormSubmit(formData: EntityFormValue<Asset>): void {
    const documentationUrl = typeof formData['documentationUrl'] === 'string' ? formData['documentationUrl'] : '';
    const payload: EntityFormValue<Asset> = { ...formData, documentationUrl };

    if (this.dialogRef) {
      this.dialogRef.close(payload);
    } else {
      this.formSubmit.emit(payload);
    }
  }

  onFormCancel(): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    } else {
      this.formCancel.emit();
    }
  }

  onDeleteRequest(entity: Asset): void {
    if (this.directDeleteCallback) {
      this.directDeleteCallback(entity);
      this.dialogRef?.close();
      return;
    }
    if (this.dialogRef) {
      const deleteAction: EntityFormDeleteAction<Asset> = { __action: 'delete', entity };
      this.dialogRef.close(deleteAction);
    } else {
      this.deleteEntity.emit(entity);
    }
  }
}
